<template>
  <div class="flex w-full flex-col space-y-6">
    <div class="text-center">
      <h2 class="text-xl font-semibold text-gray-900">Compartir Cotización</h2>
      <p class="mt-2 text-sm text-gray-600">
        Comparte tu cotización con el cliente para que pueda revisarla y responder
      </p>
    </div>
    <div
      v-if="!isShared"
      class="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-6"
    >
      <div class="text-center">
        <div class="mx-auto mb-4 flex size-10 items-center justify-center rounded-full bg-lk-green/10 md:size-12">
          <ShareIcon class="size-5 text-lk-green md:size-6" />
        </div>

        <h3 class="mb-2 text-lg font-medium text-gray-700">¿Confirmas que quieres compartir esta cotización?</h3>

        <p class="mb-4 text-sm text-gray-500">
          Al aceptar deberás enviar al cliente un enlace para que pueda revisar la cotización.
        </p>

        <div class="mb-6 self-center rounded-md bg-amber-50 p-4">
          <div class="flex items-center">
            <ExclamationTriangleIcon class="size-5 shrink-0 text-amber-400" />
            <p class="ml-2 text-left text-sm text-amber-800 md:ml-3">
              Una vez compartida, no podrás modificar los productos de la cotización.
            </p>
          </div>
        </div>

        <div class="flex items-center justify-center space-x-3">
          <button
            class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            @click="emit('close')"
          >
            Cancelar
          </button>

          <button
            class="inline-flex items-center gap-2 rounded-md bg-lk-green px-4 py-2 text-sm font-medium text-white hover:bg-lk-green-deep focus:outline-none focus:ring-2 focus:ring-lk-green-deep focus:ring-offset-2 disabled:opacity-50"
            :disabled="sharePreOrderMutation.isLoading.value"
            @click="sharePreOrderMutation.mutate()"
          >
            <ArrowPathIcon
              v-if="sharePreOrderMutation.isLoading.value"
              class="size-4 animate-spin"
            />
            <CheckIcon
              v-else
              class="size-4"
            />
            {{ sharePreOrderMutation.isLoading.value ? 'Compartiendo...' : 'Sí, compartir' }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-else
      class="space-y-4"
    >
      <div class="rounded-lg border border-gray-200 bg-white p-6">
        <div class="mb-4 flex items-center gap-2">
          <ShareIcon class="size-5 text-gray-600" />
          <h3 class="text-lg font-medium text-gray-900">Enlace de la cotización</h3>
        </div>

        <div class="flex items-center space-x-2">
          <input
            ref="urlInput"
            :value="preOrderUrl"
            readonly
            class="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <button
            class="inline-flex items-center gap-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            @click="copyToClipBoard"
          >
            <ClipboardIcon
              v-if="!copied"
              class="size-4"
            />
            <CheckIcon
              v-else
              class="size-4"
            />
            {{ copied ? 'Copiado!' : 'Copiar' }}
          </button>
        </div>

        <p class="mt-2 text-xs text-gray-500">
          Esta cotización expira el {{ formattedExpiryDate }} ({{ daysUntilExpiration }} días restantes)
        </p>
      </div>

      <div class="flex justify-center">
        <button
          class="text-sm text-gray-500 hover:text-gray-700"
          @click="emit('close')"
        >
          Volver al listado de cotizaciones
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMutation } from 'vue-query';
import useMessageStore from '../stores/message-store';
import preOrdersApi from 'api/pre-orders';
import { ShareIcon, ClipboardIcon, CheckIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/vue/24/outline';

const props = defineProps<{
  preOrder: PreOrder;
}>();

const emit = defineEmits<{
  close: [];
  'pre-order:shared': [PreOrder];
}>();

const messageStore = useMessageStore();

const copied = ref(false);
const urlInput = ref<HTMLInputElement>();

const currentPreOrder = ref(props.preOrder);
const isShared = computed(() => currentPreOrder.value.status === 'sent');
const protocol = computed(() => (process.env.APPLICATION_HOST?.includes('http') ? '' : 'https://'));
const host = ref(`${protocol.value}${process.env.APPLICATION_HOST}`);
const preOrderUrl = computed(() => `${host.value}/pre-orders/${currentPreOrder.value.accessToken}`);

const formattedExpiryDate = computed(() => {
  if (!currentPreOrder.value.expiresAt) return '';
  return format(new Date(currentPreOrder.value.expiresAt), 'dd/MM/yyyy', { locale: es });
});

const daysUntilExpiration = computed(() => {
  const now = new Date();
  const expiresAt = new Date(currentPreOrder.value.expiresAt);
  const diffTime = Math.abs(expiresAt.getTime() - now.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
});

const sharePreOrderMutation = useMutation(() => preOrdersApi.update(currentPreOrder.value.id, { status: 'sent' }), {
  onSuccess: (response) => {
    messageStore.showMessage('Cotización confirmada exitosamente', 'success');
    currentPreOrder.value = response.data.preOrder as PreOrder;
    emit('pre-order:shared', currentPreOrder.value);
  },
  onError: (error) => {
    console.error('Error sharing pre-order:', error);
    messageStore.showMessage('Error al compartir la cotización. Intenta nuevamente.', 'error');
  },
});

function copyToClipBoard() {
  navigator.clipboard.writeText(preOrderUrl.value).then(() => {
    copied.value = true;
    messageStore.showMessage('Enlace copiado al portapapeles', 'success');
  });
}
</script>
